import { useCallback, useEffect, useState } from "react";
import { api, errMsg, itDate } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Eye, Printer, DownloadSimple, Trash, Archive, PencilSimple, FileText, Gear } from "@phosphor-icons/react";
import { ChecklistEditor, MODULI } from "@/components/ChecklistEditor";

const MESI_IT = ["Gennaio", "Febbraio", "Marzo", "Aprile", "Maggio", "Giugno",
                 "Luglio", "Agosto", "Settembre", "Ottobre", "Novembre", "Dicembre"];

const iso = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;

// le settimane di calendario (Lunedì -> Sabato, Domenica esclusa) contenute nel periodo scelto
const settimaneDi = (dal, al) => {
  const fine = new Date(`${al}T00:00:00`);
  const lunedi = new Date(`${dal}T00:00:00`);
  lunedi.setDate(lunedi.getDate() - ((lunedi.getDay() + 6) % 7));
  const out = [];
  while (lunedi <= fine) {
    const sabato = new Date(lunedi);
    sabato.setDate(sabato.getDate() + 5);
    out.push({ dal: iso(lunedi), al: iso(sabato) });
    lunedi.setDate(lunedi.getDate() + 7);
  }
  return out;
};

export default function ChecklistArchive() {
  const [rows, setRows] = useState([]);
  const anno = new Date().getFullYear();
  const [filtro, setFiltro] = useState("all");
  const [mese, setMese] = useState("all");
  const [editor, setEditor] = useState(null);
  const [giorno, setGiorno] = useState(iso(new Date()));
  const [fine, setFine] = useState(iso(new Date()));
  const [gen, setGen] = useState(false);
  const periodi = fine >= giorno ? settimaneDi(giorno, fine) : [];

  const genera = async () => {
    if (!periodi.length) { toast.error("La data finale precede quella iniziale"); return; }
    setGen(true);
    try {
      for (const s of periodi) {
        for (const modello of ["01", "02"]) {
          await api.get("/checklist-pulizie/pdf",
            { params: { dal: s.dal, al: s.al, modello }, responseType: "blob" });
        }
      }
      toast.success(periodi.length === 1
        ? `Check list generate per la settimana ${itDate(periodi[0].dal)} – ${itDate(periodi[0].al)}`
        : `Check list generate per ${periodi.length} settimane`);
      setFiltro("all");
      setMese("all");
      load();
    } catch (e) { toast.error(errMsg(e)); } finally { setGen(false); }
  };

  const stampaPeriodo = async () => {
    if (!periodi.length) { toast.error("Indica il periodo da stampare"); return; }
    const dal = periodi[0].dal;
    const al = periodi[periodi.length - 1].al;
    try {
      const r = await api.get("/checklist-pulizie/pdf-periodo",
        { params: { dal, al }, responseType: "blob" });
      const url = URL.createObjectURL(new Blob([r.data], { type: "application/pdf" }));
      const w = window.open("", "_blank");
      if (!w) { toast.error("Consenti le finestre popup per aprire il PDF"); return; }
      w.document.write(
        `<!doctype html><title>check_list_${dal}_${al}.pdf</title>`
        + `<style>html,body{margin:0;height:100%}iframe{border:0;width:100%;height:100%}</style>`
        + `<iframe src="${url}#print" onload="this.contentWindow.print()"></iframe>`);
      w.document.close();
      load();
    } catch (e) { toast.error(errMsg(e)); }
  };

  const load = useCallback(async () => {
    try {
      const params = {};
      if (filtro !== "all") params.anno = Number(filtro);
      if (filtro !== "all" && mese !== "all") params.mese = Number(mese);
      const r = await api.get("/checklist-pulizie/salvate", { params });
      setRows(r.data || []);
    } catch (e) { toast.error(errMsg(e)); }
  }, [filtro, mese]);

  useEffect(() => { load(); }, [load]);

  // una riga per settimana: i due moduli viaggiano insieme in un unico PDF
  const settimane = [];
  rows.forEach((r) => {
    const chiave = `${r.dal}_${r.al}`;
    let s = settimane.find((x) => x.chiave === chiave);
    if (!s) {
      s = { chiave, dal: r.dal, al: r.al, settimana: r.settimana, mese: r.mese, anno: r.anno,
            modificata: false, updated_at: r.updated_at, updated_by: r.updated_by || r.created_by,
            ids: [] };
      settimane.push(s);
    }
    s.ids.push(r.id);
    s.modificata = s.modificata || !!r.modificata;
    if ((r.updated_at || "") > (s.updated_at || "")) {
      s.updated_at = r.updated_at;
      s.updated_by = r.updated_by || r.created_by;
    }
  });

  const nomeSettimana = (s) =>
    `${s.dal.split("-").reverse().join("-")}_${s.al.split("-").reverse().join("-")}_check_list.pdf`;

  const apriSettimana = async (s, stampa = false) => {
    try {
      const res = await api.get("/checklist-pulizie/pdf-periodo",
        { params: { dal: s.dal, al: s.al }, responseType: "blob" });
      const url = URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      const w = window.open("", "_blank");
      if (!w) { toast.error("Consenti le finestre popup per aprire il PDF"); return; }
      w.document.write(
        `<!doctype html><title>${nomeSettimana(s)}</title>`
        + `<style>html,body{margin:0;height:100%}iframe{border:0;width:100%;height:100%}</style>`
        + `<iframe src="${url}${stampa ? "#print" : ""}" ${stampa ? 'onload="this.contentWindow.print()"' : ""}></iframe>`);
      w.document.close();
    } catch (e) { toast.error(errMsg(e)); }
  };

  const scaricaSettimana = async (s) => {
    try {
      const res = await api.get("/checklist-pulizie/pdf-periodo",
        { params: { dal: s.dal, al: s.al }, responseType: "blob" });
      const a = document.createElement("a");
      a.href = URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      a.download = nomeSettimana(s);
      document.body.appendChild(a);
      a.click();
      a.remove();
    } catch (e) { toast.error(errMsg(e)); }
  };

  const eliminaSettimana = async (s) => {
    if (!window.confirm(`Eliminare la check list della settimana ${itDate(s.dal)} – ${itDate(s.al)} (entrambi i moduli)?`)) return;
    try {
      for (const id of s.ids) await api.delete(`/checklist-pulizie/salvate/${id}`);
      toast.success("Check list della settimana eliminata");
      load();
    } catch (e) { toast.error(errMsg(e)); }
  };

  return (
    <div className="space-y-4" data-testid="checklist-archive-page">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="text-2xl sm:text-3xl font-head font-bold tracking-tight">Archivio Check List</h1>
          <p className="text-sm text-muted-foreground mt-1 flex items-center gap-1.5">
            <Archive size={15} /> Indica da quale a quale data generare: nascono le check list di tutte
            le settimane di calendario (Lunedì → Sabato, Domenica esclusa) e restano qui, ordinate per settimana.
          </p>
        </div>
        <div className="mobile-stack flex flex-wrap gap-2 items-center">
          <div className="flex items-center gap-1.5">
            <span className="text-[10px] uppercase tracking-wider text-muted-foreground">Dal</span>
            <Input type="date" value={giorno} onChange={(e) => setGiorno(e.target.value)}
              className="h-9 w-[150px] rounded-sm" data-testid="cla-gen-date" />
            <span className="text-[10px] uppercase tracking-wider text-muted-foreground">Al</span>
            <Input type="date" value={fine} onChange={(e) => setFine(e.target.value)}
              className="h-9 w-[150px] rounded-sm" data-testid="cla-gen-date-to" />
          </div>
          <Button className="rounded-sm font-semibold h-9" disabled={gen}
            data-testid="cla-generate" onClick={genera}>
            {gen ? <Gear size={15} className="mr-1.5 animate-spin" /> : <FileText size={15} className="mr-1.5" />}
            {gen ? "GENERAZIONE..." : "GENERA CHECK LIST"}
          </Button>
          <span className="text-xs text-muted-foreground" data-testid="cla-gen-hint">
            {periodi.length === 0 ? "periodo non valido"
              : periodi.length === 1 ? `1 settimana: ${itDate(periodi[0].dal)} – ${itDate(periodi[0].al)}`
                : `${periodi.length} settimane: ${itDate(periodi[0].dal)} – ${itDate(periodi[periodi.length - 1].al)}`}
          </span>
          <Button variant="outline" className="rounded-sm font-semibold h-9"
            data-testid="cla-print-period" onClick={stampaPeriodo}>
            <Printer size={15} className="mr-1.5" /> STAMPA PERIODO
          </Button>
          <Select value={filtro} onValueChange={(v) => { setFiltro(v); if (v === "all") setMese("all"); }}>
            <SelectTrigger className="h-9 w-[150px] rounded-sm" data-testid="cla-year"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Tutti gli anni</SelectItem>
              {[anno, anno - 1, anno - 2].map((y) => <SelectItem key={y} value={String(y)}>{y}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={mese} onValueChange={setMese} disabled={filtro === "all"}>
            <SelectTrigger className="h-9 w-[170px] rounded-sm" data-testid="cla-month"><SelectValue /></SelectTrigger>
            <SelectContent className="max-h-72">
              <SelectItem value="all">Tutti i mesi</SelectItem>
              {MESI_IT.map((m, i) => <SelectItem key={m} value={String(i + 1)}>{m}</SelectItem>)}
            </SelectContent>
          </Select>
          <span className="text-xs text-muted-foreground" data-testid="cla-count">
            {rows.length} check list
          </span>
        </div>
      </div>

      <div className="rounded-sm border border-border bg-card overflow-x-auto scroll-thin">
        <table className="w-full text-sm" data-testid="cla-table">
          <thead className="bg-secondary"><tr>
            {["", "Sett. n.", "Settimana", "Mese", "Moduli", "Stato", "Ultima modifica", "Da"].map((h) => (
              <th key={h} className="px-3 py-2 text-left text-[11px] font-semibold uppercase tracking-wider text-muted-foreground whitespace-nowrap">{h}</th>
            ))}
          </tr></thead>
          <tbody>
            {settimane.map((s) => (
              <tr key={s.chiave} className="border-t border-border hover:bg-secondary/40" data-testid={`cla-row-${s.dal}`}>
                <td className="px-3 py-1.5">
                  <div className="inline-flex items-center gap-2 whitespace-nowrap">
                    <button className="text-muted-foreground hover:text-primary" title="Modifica"
                      data-testid={`cla-edit-${s.dal}`} onClick={() => setEditor(s)}><PencilSimple size={16} /></button>
                    <button className="text-muted-foreground hover:text-primary" title="Visualizza"
                      data-testid={`cla-view-${s.dal}`} onClick={() => apriSettimana(s)}><Eye size={16} /></button>
                    <button className="text-muted-foreground hover:text-primary" title="Stampa"
                      data-testid={`cla-print-${s.dal}`} onClick={() => apriSettimana(s, true)}><Printer size={16} /></button>
                    <button className="text-muted-foreground hover:text-primary" title="Scarica"
                      data-testid={`cla-download-${s.dal}`} onClick={() => scaricaSettimana(s)}><DownloadSimple size={16} /></button>
                    <button className="text-muted-foreground hover:text-rose-500" title="Elimina"
                      data-testid={`cla-delete-${s.dal}`} onClick={() => eliminaSettimana(s)}><Trash size={16} /></button>
                  </div>
                </td>
                <td className="px-3 py-1.5 font-semibold tabular-nums" data-testid={`cla-week-${s.dal}`}>
                  {s.settimana}
                </td>
                <td className="px-3 py-1.5 font-semibold whitespace-nowrap">{itDate(s.dal)} – {itDate(s.al)}</td>
                <td className="px-3 py-1.5 whitespace-nowrap">{MESI_IT[(s.mese || 1) - 1]} {s.anno}</td>
                <td className="px-3 py-1.5 font-mono text-[11px] leading-tight">
                  {MODULI.map((m) => <div key={m.modello}>{m.titolo}</div>)}
                </td>
                <td className="px-3 py-1.5 text-xs font-semibold">
                  {s.modificata
                    ? <span className="text-amber-600">MODIFICATA</span>
                    : <span className="text-muted-foreground">GENERATA</span>}
                </td>
                <td className="px-3 py-1.5 whitespace-nowrap">{itDate(s.updated_at, true)}</td>
                <td className="px-3 py-1.5 text-xs text-muted-foreground">{s.updated_by || "—"}</td>
              </tr>
            ))}
            {settimane.length === 0 && (
              <tr><td colSpan={8} className="px-3 py-8 text-center text-muted-foreground" data-testid="cla-empty">
                Nessuna check list per il periodo selezionato: indica le date e premi GENERA CHECK LIST
              </td></tr>
            )}
          </tbody>
        </table>
      </div>
      {editor && (
        <ChecklistEditor dal={editor.dal} al={editor.al}
          onSaved={() => load()} onClose={() => setEditor(null)} />
      )}
    </div>
  );
}
